import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import Helper from './Helper';

import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryDesktop as DesktopMediaGallery } from './subcomponents';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { dataModel } from './dataModel';
import { mediaGalleryDesktopPropTypes, mediaGalleryDesktopDefaultProps } from './MediaGalleryPropTypes';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';

export const MediaGalleryDesktop = ({
  collectionId,
  hideProductImagesLabel,
  highlight,
  highRes,
  hover,
  itemId,
  marqueeRef,
  marqueeRowRef,
  overrideData,
  showFavorites,
  showLoader,
  showAvataarDefault,
  showAvataarAR,
  onAvataarButtonClick
}) => {
  const { media: overrideMedia, ...overrideDataForOverlay } = overrideData || {};
  const {
    externalMedia, configuredProductLabel
  } = useLifeCycleEventBus('configurator.configurator_media_gallery_event');
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const storeId = useStoreId();
  const [superSkuHoverId, setSuperSkuHoverId] = useState('');
  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setSuperSkuHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  const [superSkuHoveredProductRequest, superSkuHoveredProductResponse] = useLazyDataModel('product', { });

  useEffect(() => {
    if (superSkuHoverId) {
      superSkuHoveredProductRequest({ variables: { itemId: superSkuHoverId } });
    }
  }, [superSkuHoverId]);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, []);

  const productData = useDataModel('product', { variables: { itemId }, skip: !itemId || overrideData });
  const collectionData = useDataModel('collection', {
    variables: { collectionId, storeId },
    skip: !collectionId || overrideData
  });

  const { product } = productData?.data || {};
  const { collection } = collectionData?.data || {};

  // for swatch Hover
  const mainSuperSkuHover = Helper.getMainSuperSkuHover({
    itemId,
    superSkuHoveredProductResponse,
    collectionData,
    overrideData,
    externalMedia,
    showAvataarDefault,
    superSkuHoverId
  });
  const { displayedMedia, ...apiResults } = Helper.getDisplayedMedia({
    isMobile: false,
    itemId,
    productData,
    collectionId,
    collectionData,
    externalMedia,
    overrideData,
    placeholder: showLoader && <MediaGalleryLoader isMobile={false} />,
    showAvataarDefault
  });
  const { augmentedRealityIndex, zoom } = displayedMedia;

  useEffect(() => {
    // TODO: Hook
    if (highRes && zoom) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }
      }).catch(() => { });
    }
  }, [itemId, !!zoom]);

  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <DesktopMediaGallery
      media={mediaMapRef.current}
      collection={collection}
      product={product}
      overrideData={overrideDataForOverlay}
      totalThumbnailsToShow={6}
      hideProductImagesLabel={hideProductImagesLabel}
      mainSuperSkuHover={mainSuperSkuHover}
      showFavorites={showFavorites}
      marqueeRef={marqueeRef}
      marqueeRowRef={marqueeRowRef}
      configuredProductLabel={configuredProductLabel}
      showAvataarDefault={showAvataarDefault}
      showAvataarAR={showAvataarAR}
      augmentedRealityIndex={augmentedRealityIndex}
      onAvataarButtonClick={onAvataarButtonClick}
    />
  );
};

MediaGalleryDesktop.displayName = 'MediaGalleryDesktop';

MediaGalleryDesktop.propTypes = mediaGalleryDesktopPropTypes;

MediaGalleryDesktop.defaultProps = mediaGalleryDesktopDefaultProps;

MediaGalleryDesktop.dataModel = dataModel;
